import { PaytrieAPI } from '@paytrie/client'
import { z } from 'zod'

import i18n from '@/utils/i18n'

const SOURCE = 'widget'

export const track = (
  eventName: string,
  payload?: Record<string, unknown>
): void => {
  try {
    PaytrieAPI.recordEvent(
      {
        eventName,
        source: SOURCE,
        ...(payload && Object.keys(payload).length > 0 ? { payload } : {}),
      },
      { keepalive: true }
    ).catch(() => {})
  } catch {
    //
  }
}

const readAttributionFromLocation = (): Record<string, string> => {
  const params = new URLSearchParams(window.location.search)
  const get = (key: string) => params.get(key) ?? undefined
  const referrer =
    typeof document !== 'undefined' && document.referrer
      ? document.referrer
      : undefined
  let embedOrigin: string | undefined
  try {
    if (referrer) embedOrigin = new URL(referrer).origin
  } catch {
    //
  }

  const attribution: Record<string, string> = {}
  const partner = get('parent') ?? get('vendorId')
  if (partner) attribution.partner = partner
  if (referrer) attribution.referrer = referrer
  if (embedOrigin) attribution.embed_origin = embedOrigin
  const utmSource = get('utm_source')
  const utmMedium = get('utm_medium')
  const utmCampaign = get('utm_campaign')
  if (utmSource) attribution.utm_source = utmSource
  if (utmMedium) attribution.utm_medium = utmMedium
  if (utmCampaign) attribution.utm_campaign = utmCampaign
  return attribution
}

const readTheme = (): 'dark' | 'light' =>
  document.documentElement.classList.contains('dark') ? 'dark' : 'light'

let previousLanguage: string | null = null
let previousTheme: 'dark' | 'light' | null = null
let listenersInitialized = false

export const setupAnalyticsListeners = (): void => {
  if (listenersInitialized) return
  listenersInitialized = true

  track('application_opened', readAttributionFromLocation())

  previousLanguage = i18n.language ?? null
  i18n.on('languageChanged', (currentLanguage: string) => {
    if (previousLanguage === currentLanguage) return
    track('language_changed', {
      current_language: currentLanguage,
      previous_language: previousLanguage,
    })
    previousLanguage = currentLanguage
  })

  previousTheme = readTheme()
  const themeObserver = new MutationObserver(() => {
    const currentTheme = readTheme()
    if (currentTheme === previousTheme) return
    track('theme_changed', {
      current_theme: currentTheme,
      previous_theme: previousTheme,
    })
    previousTheme = currentTheme
  })
  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  })
}

let previousScreen: string | null = null

const idSegment = z.union([z.uuid(), z.string().regex(/^\d+$/)])

const getIsIdSegment = (segment: string): boolean =>
  idSegment.safeParse(segment).success

export const toScreenName = (pathname: string): string =>
  pathname
    .split('/')
    .map((segment) => (getIsIdSegment(segment) ? ':id' : segment))
    .join('/')

export const trackScreenChange = (currentScreen: string): void => {
  const screenName = toScreenName(currentScreen)
  if (screenName === previousScreen) return
  track('screen_changed', {
    current_screen: screenName,
    previous_screen: previousScreen,
  })
  previousScreen = screenName
}

export type OnboardingStep =
  | 'email_entry'
  | 'external_email_verify'
  | 'phone_entry'
  | 'sms_code_entry'
  | 'risk_disclosure'
  | 'personal_information'
  | 'occupation_and_purpose'
  | 'funds_and_wallet'
  | 'referral_and_experience'
  | 'fraud_warning'
  | 'kyc_method_selection'
  | 'external_kyc_redirect'
  | 'registration_complete'

export const trackOnboardingStepViewed = (step: OnboardingStep): void => {
  track('onboarding_step_viewed', { step })
}

export const trackOnboardingStepSubmitted = (step: OnboardingStep): void => {
  track('onboarding_step_submitted', { step })
}

export const trackOnboardingStepCompleted = (step: OnboardingStep): void => {
  track('onboarding_step_completed', { step })
}

export const trackOnboardingStepFailed = (
  step: OnboardingStep,
  reason: string
): void => {
  track('onboarding_step_failed', { step, reason })
}

export const trackOnboardingFieldValidationFailed = (
  step: OnboardingStep,
  field: string,
  code: string
): void => {
  track('onboarding_field_validation_failed', { step, field, code })
}

export const trackOnboardingVerificationCodeResent = (
  channel: 'email' | 'sms'
): void => {
  track('onboarding_verification_code_resent', { channel })
}

export const trackOnboardingVerificationCodeFailed = (
  channel: 'email' | 'sms',
  reason: string
): void => {
  track('onboarding_verification_code_failed', { channel, reason })
}

export const trackOnboardingCompleted = (): void => {
  track('onboarding_completed')
}

export type OtcSide = 'buy' | 'sell'

export type OtcFormStep = 'details' | 'bank_details'

export type OtcScreen = 'submit' | 'status' | 'status-not-found'

export type OtcBankMode = 'saved' | 'new'

export const trackOtcRequestStarted = ({
  side,
  leftSideLabel,
  rightSideLabel,
  leftSideValue,
}: {
  side: OtcSide
  leftSideLabel: string
  rightSideLabel: string
  leftSideValue: number
}): void => {
  track('otc_request_started', {
    side,
    left_side_label: leftSideLabel,
    right_side_label: rightSideLabel,
    left_side_value: leftSideValue,
  })
}

export const trackOtcStepViewed = (step: OtcFormStep, side: OtcSide): void => {
  track('otc_step_viewed', { step, side })
}

export const trackOtcStepSubmitted = (
  step: OtcFormStep,
  side: OtcSide
): void => {
  track('otc_step_submitted', { step, side })
}

export const trackOtcStepFailed = ({
  step,
  side,
  reason,
}: {
  step: OtcFormStep
  side: OtcSide
  reason: string
}): void => {
  track('otc_step_failed', { step, side, reason })
}

export const trackOtcBankAccountSelected = (mode: OtcBankMode): void => {
  track('otc_bank_account_selected', { mode })
}

export const trackOtcSavedAccountsLoadFailed = (): void => {
  track('otc_saved_accounts_load_failed')
}

export const trackOtcRequestSubmitted = ({
  otcRequestId,
  side,
  bankMode,
}: {
  otcRequestId: string
  side: OtcSide
  bankMode: OtcBankMode | null
}): void => {
  track('otc_request_submitted', {
    otc_request_id: otcRequestId,
    side,
    bank_mode: bankMode,
  })
}

export const trackOtcRequestSubmitFailed = (
  side: OtcSide,
  reason: string
): void => {
  track('otc_request_submit_failed', { side, reason })
}

export const trackOtcRequestStatusViewed = ({
  otcRequestId,
  status,
  side,
}: {
  otcRequestId: string
  status: string
  side: OtcSide
}): void => {
  track('otc_request_status_viewed', {
    otc_request_id: otcRequestId,
    status,
    side,
  })
}

export const trackOtcRequestSettled = ({
  otcRequestId,
  status,
  side,
}: {
  otcRequestId: string
  status: string
  side: OtcSide
}): void => {
  track('otc_request_settled', {
    otc_request_id: otcRequestId,
    status,
    side,
  })
}

export const trackOtcWireInstructionsToggled = (isOpen: boolean): void => {
  track('otc_wire_instructions_toggled', { is_open: isOpen })
}

export const trackOtcWireInstructionsDownloaded = (): void => {
  track('otc_wire_instructions_downloaded')
}

export const trackOtcWireInstructionsLoadFailed = (): void => {
  track('otc_wire_instructions_load_failed')
}

export const trackOtcSupportContacted = (screen: OtcScreen): void => {
  track('otc_support_contacted', { screen })
}

export const trackOtcRequestsListViewed = ({
  page,
  pageSize,
  count,
}: {
  page: number
  pageSize: number
  count: number
}): void => {
  track('otc_requests_list_viewed', {
    page,
    page_size: pageSize,
    count,
  })
}

export const trackOtcRequestOpened = ({
  otcRequestId,
  status,
  position,
}: {
  otcRequestId: string
  status: string
  position: number
}): void => {
  track('otc_request_opened', {
    otc_request_id: otcRequestId,
    status,
    position,
  })
}
